import { useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import { useAuditPayslipDownloadMutation, useLazyGetPayRollDataQuery } from "../api/payRollApi";
import { usePayslipDetailsMutation } from "../api/loginApi";
import { get_file_from_s3 } from "../utils";
import numberToWords from "./numbersToWords";
import PayslipAmountToggle, { MASKED_AMOUNT } from "./PayslipAmountToggle";

// Any mid-month day keeps the selected month intact regardless of the browser's timezone.
const PAYSLIP_REQUEST_DAY = 15;
const GRID_DATE_FORMAT = "YYYY-MM-DD";
const PAYSLIP_DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm:ss";
const PERIOD_DISPLAY_FORMAT = "MMMM YYYY";
const PERIOD_FILE_FORMAT = "MMM_YYYY";
const DEFAULT_FILE_EXTENSION = "pdf";
const EMPLOYEES_LOAD_ERROR = "Unable to load employees for the selected month";
const PAYSLIP_LOAD_ERROR = "Unable to fetch the payslip";
const DOWNLOAD_ERROR = "Unable to download the payslip";
const NO_PAYSLIP_MESSAGE = "No payslip found for the selected employee and month";
const NOT_RELEASED_LABEL = "Not released";

// When masked, the real value is never rendered, so it is not exposed in the DOM or screenshots.
const formatAmount = (value, masked = false) => {
  if (masked) {
    return MASKED_AMOUNT;
  }
  return value === null || value === undefined || value === ""
    ? "--"
    : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getErrorMessage = (error, fallback) => error?.data?.message || fallback;

const getFileExtension = (fileName) => {
  const lastDot = fileName?.lastIndexOf(".") ?? -1;
  return lastDot > -1 ? fileName.substring(lastDot + 1) : DEFAULT_FILE_EXTENSION;
};

const AmountBreakdown = ({ title, items, total, masked }) => (
  <div className="flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
    <div className="flex justify-between bg-gray-50 border-b border-gray-200 px-6 py-4">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{title}</h3>
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount (₹)</span>
    </div>
    <div className="flex-1 divide-y divide-gray-100">
      {(items || []).map((item) => (
        <div key={item?.itemKey || item?.displayText} className="flex justify-between px-6 py-3 text-sm">
          <span className="text-gray-700">{item?.displayText || "--"}</span>
          <span className="text-gray-900 font-medium">{formatAmount(item?.value, masked)}</span>
        </div>
      ))}
    </div>
    <div className="flex justify-between bg-gray-100 px-6 py-3 text-sm font-semibold text-gray-900">
      <span>TOTAL</span>
      <span>{formatAmount(total, masked)}</span>
    </div>
  </div>
);

const AdminPayslips = () => {
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [areAmountsVisible, setAreAmountsVisible] = useState(false);
  const requestedMonthRef = useRef(null);

  const [fetchEmployees, { isFetching: isFetchingEmployees }] = useLazyGetPayRollDataQuery();
  const [fetchPayslip, { data: payslipResponse, isLoading: isFetchingPayslip, reset: resetPayslip }] =
    usePayslipDetailsMutation();
  const [auditPayslipDownload] = useAuditPayslipDownloadMutation();

  const buildPayslipRequest = (employee) => ({
    employeeId: employee.id,
    dateTime: selectedMonth.date(PAYSLIP_REQUEST_DAY).startOf("day").format(PAYSLIP_DATE_TIME_FORMAT),
  });

  const payslip = payslipResponse?.data;
  const hasPayslip = Object.keys(payslip || {}).length > 0;
  const s3FileName = payslip?.paySlipDetails?.s3Document?.fileName;

  const loadEmployees = (month) => {
    const payrollProcessedDate = month.format(GRID_DATE_FORMAT);
    requestedMonthRef.current = payrollProcessedDate;
    setEmployees([]);
    fetchEmployees({ payrollProcessedDate })
      .unwrap()
      .then((response) => {
        // Ignore a slow response for a month the admin has already moved away from.
        if (requestedMonthRef.current === payrollProcessedDate) {
          // Employees with a downloadable payslip first; processed-but-unreleased ones follow, disabled.
          const processedEmployees = (response?.data || []).filter((employee) => employee.processedEmployee);
          setEmployees(
            [...processedEmployees].sort((a, b) => Number(Boolean(b.payslipAvailable)) - Number(Boolean(a.payslipAvailable)))
          );
        }
      })
      .catch((error) => toast.error(getErrorMessage(error, EMPLOYEES_LOAD_ERROR)));
  };

  // Initial load for the default (current) month only; later month changes go through handleMonthChange.
  useEffect(() => {
    loadEmployees(selectedMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMonthChange = (month) => {
    if (!month?.isValid() || month.isSame(selectedMonth, "month")) {
      return;
    }
    setSelectedMonth(month);
    setSelectedEmployee(null);
    setAreAmountsVisible(false);
    resetPayslip();
    loadEmployees(month);
  };

  const handleEmployeeChange = (_event, employee) => {
    setSelectedEmployee(employee);
    setAreAmountsVisible(false);
    resetPayslip();
    if (!employee) {
      return;
    }
    fetchPayslip(buildPayslipRequest(employee))
      .unwrap()
      .catch((error) => toast.error(getErrorMessage(error, PAYSLIP_LOAD_ERROR)));
  };

  const handleDownload = async () => {
    if (!s3FileName) {
      toast.error(DOWNLOAD_ERROR);
      return;
    }
    setIsDownloading(true);
    try {
      // The download is recorded before the file is fetched; if recording fails, nothing is downloaded.
      try {
        await auditPayslipDownload(buildPayslipRequest(selectedEmployee)).unwrap();
      } catch (auditError) {
        toast.error(getErrorMessage(auditError, DOWNLOAD_ERROR));
        return;
      }
      const presignedUrl = await get_file_from_s3(s3FileName);
      if (!presignedUrl) {
        return;
      }
      const response = await fetch(presignedUrl);
      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const fileName = `Payslip_${selectedEmployee.employeeId}_${selectedMonth.format(PERIOD_FILE_FORMAT)}.${getFileExtension(s3FileName)}`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Error downloading payslip:", error);
      toast.error(DOWNLOAD_ERROR);
    } finally {
      setIsDownloading(false);
    }
  };

  const netPayInWords = useMemo(
    () => (payslip?.totalNetPay ? numberToWords(Math.round(payslip.totalNetPay)) : ""),
    [payslip?.totalNetPay]
  );

  const renderPayslip = () => {
    if (!selectedEmployee) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Employee Selected</h3>
          <p className="text-gray-500">Select a month and an employee to view their payslip</p>
        </div>
      );
    }
    if (isFetchingPayslip) {
      return (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent"></div>
        </div>
      );
    }
    if (!hasPayslip) {
      return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Payslip</h3>
          <p className="text-gray-500">{payslipResponse?.message || NO_PAYSLIP_MESSAGE}</p>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-sm text-gray-500">
              Net Pay for {selectedMonth.format(PERIOD_DISPLAY_FORMAT)}
            </p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-green-700">₹ {formatAmount(payslip.totalNetPay, !areAmountsVisible)}</p>
              <PayslipAmountToggle
                visible={areAmountsVisible}
                onToggle={() => setAreAmountsVisible((visible) => !visible)}
              />
            </div>
            {/* The amount in words reveals the net pay, so it is masked together with the figures. */}
            {areAmountsVisible && netPayInWords && <p className="text-sm text-gray-500">{netPayInWords}</p>}
            <p className="text-sm text-gray-700 mt-2">
              {selectedEmployee.employeeDisplayName}
              {payslip.daysInMonth != null && (
                <span className="text-gray-500">
                  {" "}· Days in month: {payslip.daysInMonth} · LOP days: {payslip.lossOfPay ?? 0}
                </span>
              )}
            </p>
          </div>
          <button
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
            disabled={!s3FileName || isDownloading}
            title={s3FileName ? "Download payslip" : "Payslip document is not available"}
            onClick={handleDownload}
          >
            {isDownloading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Downloading...
              </>
            ) : (
              "Download Payslip"
            )}
          </button>
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <AmountBreakdown
            title="Earnings"
            items={payslip.totalGrossPayDetails}
            total={payslip.totalGrossPay}
            masked={!areAmountsVisible}
          />
          <AmountBreakdown
            title="Deductions"
            items={payslip.totalDeductionDetails}
            total={payslip.totalDeduction}
            masked={!areAmountsVisible}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          <div className="w-full sm:max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">Payroll Month</label>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={selectedMonth}
                className="w-full"
                format="MM/YYYY"
                views={["month", "year"]}
                openTo="month"
                onChange={handleMonthChange}
                disableFuture
                slotProps={{ textField: { readOnly: true, size: "small" } }}
              />
            </LocalizationProvider>
          </div>
          <div className="w-full sm:flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Employee</label>
            <Autocomplete
              size="small"
              options={employees}
              value={selectedEmployee}
              loading={isFetchingEmployees}
              onChange={handleEmployeeChange}
              getOptionLabel={(employee) => employee?.employeeDisplayName || ""}
              getOptionDisabled={(employee) => !employee.payslipAvailable}
              renderOption={(props, employee) => (
                <li {...props} key={employee.id} className={`${props.className} flex justify-between gap-4`}>
                  <span>{employee.employeeDisplayName}</span>
                  {!employee.payslipAvailable && (
                    <span className="text-xs font-medium text-yellow-700 bg-yellow-50 rounded px-2 py-0.5">
                      {NOT_RELEASED_LABEL}
                    </span>
                  )}
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No processed payroll for this month"
              renderInput={(params) => <TextField {...params} placeholder="Search by name or employee ID" />}
            />
          </div>
        </div>
      </div>
      {renderPayslip()}
    </div>
  );
};

export default AdminPayslips;
